/**
 * SidebarParentItem Component
 *
 * Collapsible parent navigation item for the sidebar.
 * Supports nested children with expand/collapse functionality.
 */

import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { SidebarItem } from './SidebarItem';
import type { NavigationItem } from '../utils/navigationItems';

interface SidebarParentItemProps {
  item: NavigationItem;
  onClick?: () => void;
}

export const SidebarParentItem: React.FC<SidebarParentItemProps> = ({
  item,
  onClick,
}) => {
  const location = useLocation();
  const [open, setOpen] = useState(() => {
    // Auto-expand if any child is active
    return item.children?.some((child) =>
      location.pathname === child.path || location.pathname.startsWith(child.path + '/')
    ) || false;
  });

  // Check if any child is active
  const isActive = item.children?.some((child) =>
    location.pathname === child.path || location.pathname.startsWith(child.path + '/')
  ) || false;

  const handleClick = () => {
    setOpen(!open);
  };

  return (
    <>
      <ListItem disablePadding sx={{ display: 'block' }}>
        <ListItemButton
          onClick={handleClick}
          selected={isActive}
          sx={{
            minHeight: 48,
            px: 2.5,
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: 3,
              justifyContent: 'center',
              color: 'inherit',
            }}
          >
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: isActive ? 600 : 400,
            }}
          />
          {open ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
      </ListItem>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {item.children?.map((child) => (
            <SidebarItem
              key={child.path}
              label={child.label}
              path={child.path}
              icon={child.icon}
              onClick={onClick}
              sx={{ pl: 4 }}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
};
